import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type IntroCall,
  type Person,
  type Prospect,
  type ProspectDetail,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  AbsentNotice,
  Button,
  buttonClasses,
  Empty,
  Failed,
  Field,
  Label,
  Mark,
  Panel,
  Select,
  SkeletonRows,
  TextArea,
} from "../components/primitives";
import { day } from "../lib/format";
import { roleLabel, useSession } from "../lib/session";
import {
  CALL_OUTLINE,
  draftRecap,
  missingAnswers,
  type OutlineField,
} from "./catchOutline";

/**
 * Catch — the phase before a client is a client.
 *
 * Two surfaces on one route, because they are two halves of one job: the
 * pipeline of prospects, and the call itself being run against a fixed
 * structure. Opening a prospect from the list drops into the call runner; the
 * rail item is unchanged either way, because you are still in Catch.
 *
 * **Who sees this at all.** `FIRM_NAV` hides the item from consultants and
 * clients, and that is courtesy only — the refusal is 050's policies, which
 * admit Super Admin and Principal Partner to the whole pipeline and a Project
 * Lead to the prospects they own. Nothing in this file checks a role; a Lead
 * simply receives a shorter list, which is the database answering.
 *
 * **A prospect is not a client until an engagement is opened for it.** This
 * screen deliberately has no "make them a client" button. A client is born in
 * `POST /api/engagements`, in one transaction with the engagement, and a second
 * door here would be a second way for one relationship to become two clients
 * with two permanent filing codes. The converted banner points at Engagements
 * instead.
 *
 * **Where results of actions live.** `People.tsx` carried a defect fixed on
 * 2026-10-27: a component that triggers `reload()` cannot be the one holding
 * the result of what it just did, because `useResource`'s reload unmounts the
 * list while it refetches. So the "opened" confirmation lives here, above the
 * list, and never inside a row.
 */

export function Catch() {
  const prospects = useResource<Prospect[]>(
    () => api.get<Prospect[]>("/api/prospects"),
    [],
  );
  const people = useResource<Person[]>(() => api.get<Person[]>("/api/people"), []);

  /** The prospect whose call is being run, or null for the list. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  /** Held HERE, not in a row — see the note at the top of this file. */
  const [justOpened, setJustOpened] = useState<string | null>(null);
  const [justDeleted, setJustDeleted] = useState<string | null>(null);

  return (
    <AppShell>
      <ScreenHead
        title="Catch"
        sub="Track prospects and their introductory calls. A prospect becomes a client when you open an engagement for them."
      >
        {openId === null && !adding && (
          <Button tone="primary" onClick={() => setAdding(true)}>
            Open a prospect
          </Button>
        )}
        {openId !== null && (
          <Button onClick={() => setOpenId(null)}>Back to the pipeline</Button>
        )}
      </ScreenHead>

      {justOpened && openId === null && (
        <Panel className="mb-7 p-5">
          <Label as="h2">Opened</Label>
          <p className="mt-2 text-base text-ink">{justOpened}</p>
          <p className="mt-1 max-w-[62ch] text-xs leading-[1.1rem] text-muted">
            It is in the pipeline below. Open it to run the introductory call.
          </p>
        </Panel>
      )}

      {justDeleted && openId === null && (
        <Panel className="mb-7 p-5">
          <Label as="h2">Deleted permanently</Label>
          <p className="mt-2 text-base text-ink">{justDeleted}</p>
          <p className="mt-1 max-w-[62ch] text-xs leading-[1.1rem] text-muted">
            The prospect, its contacts and its introductory calls have been
            removed from Catch.
          </p>
        </Panel>
      )}

      {adding && (
        <NewProspect
          people={people.resource.state === "ready" ? people.resource.data : []}
          onCancel={() => setAdding(false)}
          onCreated={(name) => {
            setJustOpened(name);
            setJustDeleted(null);
            setAdding(false);
            prospects.reload();
          }}
        />
      )}

      {openId !== null ? (
        <CallRunner
          prospectId={openId}
          people={people.resource.state === "ready" ? people.resource.data : []}
          onChanged={() => prospects.reload()}
          onDeleted={(name) => {
            setOpenId(null);
            setJustOpened(null);
            setJustDeleted(name);
            prospects.reload();
          }}
        />
      ) : (
        <>
          {prospects.resource.state === "loading" && <SkeletonRows rows={4} />}
          {prospects.resource.state === "failed" && (
            <Failed
              message={prospects.resource.message}
              correlationId={prospects.resource.correlationId}
            />
          )}
          {prospects.resource.state === "ready" && (
            <Pipeline
              rows={prospects.resource.data}
              onOpen={(id) => {
                setJustOpened(null);
                setJustDeleted(null);
                setOpenId(id);
              }}
            />
          )}
        </>
      )}
    </AppShell>
  );
}

// ---------------------------------------------------------------- the list --

function statusTone(status: Prospect["status"]) {
  if (status === "converted") return "success" as const;
  if (status === "declined") return "spent" as const;
  return "live" as const;
}

function statusWord(status: Prospect["status"]) {
  if (status === "converted") return "Client";
  if (status === "declined") return "Declined";
  return "Open";
}

function Pipeline({
  rows,
  onOpen,
}: {
  rows: Prospect[];
  onOpen: (id: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <Empty title="No prospects yet">
        {/* Says nothing about whether anything exists beyond this caller's
            reach. A Project Lead sees their own prospects, so "none" here
            means none of theirs — not none in the firm. */}
        Open a prospect when an introductory call is booked. The call guide
        will be ready inside.
      </Empty>
    );
  }

  return (
    <div className="border-t border-line">
      {rows.map((p) => {
        // A next step whose date has passed is the single most useful thing on
        // this screen: it is a commitment the firm made and has not kept.
        const late =
          p.nextStepBy !== null &&
          p.status === "open" &&
          p.nextStepBy < new Date().toISOString().slice(0, 10);

        return (
          /* A div wrapping a button, not one button wrapping everything.
             The row needs an action beside it — "turn into an engagement" —
             and a <button> inside a <button> is invalid markup that browsers
             resolve by dropping one of them, usually the one you wanted.
             `Engagements.tsx` already uses this shape for the same reason:
             the name is the control that opens the row, and anything else on
             the row is its own control. */
          <div
            key={p.id}
            className="flex w-full flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-line px-1 py-4 text-left transition-colors duration-150 ease-damped hover:bg-control"
          >
            <button
              type="button"
              onClick={() => onOpen(p.id)}
              className="min-w-0 flex-1 text-left"
            >
              <span className="block text-base text-ink">{p.name}</span>
              <span className="mt-1 block text-xs text-muted">
                {[
                  p.sector,
                  p.owner,
                  p.calls === 0
                    ? "no call yet"
                    : `${p.calls} call${p.calls === 1 ? "" : "s"}`,
                  p.lastCalledOn ? `last ${day(p.lastCalledOn)}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              {p.nextStep && (
                <span className="mt-1 block max-w-[62ch] text-xs leading-[1.1rem] text-muted">
                  Next: {p.nextStep}
                  {p.nextStepBy && ` — by ${day(p.nextStepBy)}`}
                </span>
              )}
            </button>
            <span className="flex shrink-0 flex-wrap items-center gap-2">
              {late && <Mark tone="danger">Overdue</Mark>}
              {/* The call is done and this one is waiting to be started.
                  Shown beside the status rather than instead of it: "Open"
                  says where the prospect stands, "Ready" says what to do. */}
              {p.status === "open" && p.completedCalls > 0 && (
                <Mark tone="success">Ready</Mark>
              )}
              <Mark tone={statusTone(p.status)}>{statusWord(p.status)}</Mark>
              <TurnIntoEngagement
                prospect={p}
                completed={p.completedCalls > 0}
                compact
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------- opening one --

function NewProspect({
  people,
  onCreated,
  onCancel,
}: {
  people: Person[];
  onCreated: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [country, setCountry] = useState("");
  const [source, setSource] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");

  const { invoke, pending, refusal } = useAction(async () => {
    await api.post<{ id: string }>("/api/prospects", {
      name: name.trim(),
      sector: sector.trim(),
      country: country.trim(),
      source: source.trim(),
      // Empty means "me". A Lead naming somebody else is refused by
      // prospect_insert, not by a branch here.
      ...(ownerUserId ? { ownerUserId } : {}),
    });
    onCreated(name.trim());
  });

  const submit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void invoke();
    },
    [invoke],
  );

  // Firm-side and not suspended. A client cannot own a prospect, and a
  // suspended person cannot be reached by any policy — offering either would
  // be a control whose refusal arrives at the end of a filled-in form.
  const owners = people.filter((p) => p.role !== "client" && !p.isSuspended);

  return (
    <form onSubmit={submit} className="mb-9 border border-line-strong p-6">
      <Label as="h2">Open a prospect</Label>
      <p className="mt-2 max-w-[62ch] text-xs leading-[1.1rem] text-muted">
        Enough to hang a call on. Nothing here creates a client — that happens
        when an engagement is opened for them.
      </p>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field
          label="Organisation"
          hint="Who called, or who you are calling."
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoComplete="off"
        />
        <Field
          label="Sector"
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          autoComplete="off"
        />
        <Field
          label="Country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          autoComplete="off"
        />
        <Field
          label="How they came to us"
          hint="A referral is worth recording by name."
          value={source}
          onChange={(e) => setSource(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="mt-5 max-w-sm">
        <Select
          label="Who owns the relationship"
          hint="Leave as yourself unless you are handing it over."
          value={ownerUserId}
          onChange={(e) => setOwnerUserId(e.target.value)}
        >
          <option value="">Me</option>
          {owners.map((p) => (
            <option key={p.id} value={p.id}>
              {p.fullName} · {roleLabel(p.role)}
            </option>
          ))}
        </Select>
      </div>

      {refusal && (
        <Failed message={refusal.message} correlationId={refusal.correlationId} />
      )}

      <div className="mt-6 flex gap-3">
        <Button type="submit" tone="primary" loading={pending}>
          Open it
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------ the call ------

function CallRunner({
  prospectId,
  people,
  onChanged,
  onDeleted,
}: {
  prospectId: string;
  people: Person[];
  onChanged: () => void;
  onDeleted: (name: string) => void;
}) {
  const detail = useResource<ProspectDetail>(
    () => api.get<ProspectDetail>(`/api/prospects/${prospectId}`),
    [prospectId],
  );

  const { invoke: start, pending: starting } = useAction(async () => {
    await api.post<{ id: string }>(`/api/prospects/${prospectId}/calls`, {});
    detail.reload();
    onChanged();
  });

  if (detail.resource.state === "loading") return <SkeletonRows rows={6} />;
  // 404, which for a prospect means "not yours or not there" — the server
  // refuses to distinguish them, and so does this.
  if (detail.resource.state === "absent") return <AbsentNotice />;
  if (detail.resource.state === "failed") {
    return (
      <Failed
        message={detail.resource.message}
        correlationId={detail.resource.correlationId}
      />
    );
  }

  const { prospect, contacts, calls } = detail.resource.data;
  // The call being worked on: the newest draft, else the newest call at all.
  const live = calls.find((c: IntroCall) => c.status === "draft") ?? calls[0];

  return (
    <div>
      <Panel className="p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <div className="min-w-0">
            <Label as="h2">Prospect</Label>
            <p className="mt-2 text-xl text-ink">{prospect.name}</p>
            <p className="mt-1 text-xs text-muted">
              {[prospect.sector, prospect.country, prospect.source, prospect.owner]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <span className="flex shrink-0 flex-col items-end gap-3">
            <Mark tone={statusTone(prospect.status)}>
              {statusWord(prospect.status)}
            </Mark>
            <TurnIntoEngagement
              prospect={prospect}
              completed={calls.some((c: IntroCall) => c.status === "complete")}
              calls={calls.length}
            />
          </span>
        </div>

        {prospect.status === "converted" && (
          <p className="mt-4 max-w-[62ch] border-t border-line pt-4 text-xs leading-[1.1rem] text-muted">
            This prospect became a client
            {prospect.convertedAt && ` on ${day(prospect.convertedAt)}`}. The
            engagement and everything since live under Engagements; this record
            is kept because it is where the work came from.
          </p>
        )}
        {prospect.status === "declined" && (
          <p className="mt-4 max-w-[62ch] border-t border-line pt-4 text-xs leading-[1.1rem] text-muted">
            Declined{prospect.declinedAt && ` on ${day(prospect.declinedAt)}`}
            {prospect.declinedReason && ` — ${prospect.declinedReason}`}
          </p>
        )}

        <DeleteProspect prospect={prospect} onDeleted={onDeleted} />
      </Panel>

      <Contacts
        prospectId={prospectId}
        contacts={contacts}
        onAdded={() => detail.reload()}
      />

      {!live ? (
        <Empty
          title="No call recorded yet"
          action={
            <Button tone="primary" loading={starting} onClick={() => void start()}>
              Start the introductory call
            </Button>
          }
        >
          The six-stage structure opens as soon as you start. You can fill it in
          during the call or straight after it.
        </Empty>
      ) : (
        <CallForm
          call={live}
          people={people}
          onCompleted={() => {
            detail.reload();
            onChanged();
          }}
        />
      )}

      {calls.length > 1 && (
        <div className="mt-9">
          <Label as="h2">Earlier calls</Label>
          <div className="mt-4 border-t border-line">
            {calls
              .filter((c: IntroCall) => c.id !== live?.id)
              .map((c: IntroCall) => (
                <div
                  key={c.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-line py-3"
                >
                  <span className="text-base text-ink">{day(c.calledOn)}</span>
                  <span className="text-xs text-muted">
                    {c.ranByName ?? "—"}
                  </span>
                  <Mark tone={c.status === "complete" ? "success" : "warning"}>
                    {c.status === "complete" ? "Complete" : "Draft"}
                  </Mark>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The one irreversible action in Catch.
 *
 * It stays quiet until somebody asks for it, then names the exact records that
 * disappear and requires the prospect's name. The server and migration remain
 * authoritative: this confirmation prevents slips, it does not grant access.
 */
function DeleteProspect({
  prospect,
  onDeleted,
}: {
  prospect: Prospect;
  onDeleted: (name: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");

  const deletion = useAction(async () => {
    await api.del<void>(`/api/prospects/${prospect.id}`);
    onDeleted(prospect.name);
  });

  // Once a prospect became a client, its Catch record is the origin of a live
  // organisation and engagement. Migration 055 refuses deletion too; omitting
  // the control here avoids offering an action that can never be valid.
  if (prospect.status === "converted") return null;

  return (
    <div className="mt-5 border-t border-line pt-5">
      {!confirming ? (
        <Button tone="danger" onClick={() => setConfirming(true)}>
          Delete prospect
        </Button>
      ) : (
        <div className="max-w-2xl border-l-2 border-danger pl-4">
          <p className="text-base text-ink">
            Permanently delete {prospect.name} from Catch?
          </p>
          <p className="mt-2 text-sm text-secondary">
            The prospect, every contact and every introductory call will be
            removed. This cannot be undone.
          </p>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <Field
              label="Type the prospect name to confirm"
              hint={prospect.name}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
            <Button
              tone="danger"
              disabled={typed.trim() !== prospect.name}
              loading={deletion.pending}
              onClick={() => void deletion.invoke()}
            >
              Delete permanently
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setConfirming(false);
                setTyped("");
                deletion.clearRefusal();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {deletion.refusal && (
        <p className="mt-4 max-w-[62ch] border-l-2 border-danger pl-4 text-sm text-ink">
          {deletion.refusal.message}
        </p>
      )}
    </div>
  );
}

function Contacts({
  prospectId,
  contacts,
  onAdded,
}: {
  prospectId: string;
  contacts: ProspectDetail["contacts"];
  onAdded: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [fullName, setFullName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isDecisionMaker, setIsDecisionMaker] = useState(false);

  const { invoke, pending, refusal } = useAction(async () => {
    await api.post<{ id: string }>(`/api/prospects/${prospectId}/contacts`, {
      fullName: fullName.trim(),
      roleTitle: roleTitle.trim(),
      email: email.trim(),
      phone: phone.trim(),
      isDecisionMaker,
    });
    setFullName("");
    setRoleTitle("");
    setEmail("");
    setPhone("");
    setIsDecisionMaker(false);
    setAdding(false);
    onAdded();
  });

  return (
    <div className="mt-9">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <Label as="h2">Who we have spoken to</Label>
        {!adding && (
          <Button onClick={() => setAdding(true)}>Add someone</Button>
        )}
      </div>

      {contacts.length === 0 && !adding && (
        <Empty>
          Everyone recorded here becomes a client contact if this prospect
          becomes a client.
        </Empty>
      )}

      {contacts.length > 0 && (
        <div className="mt-4 border-t border-line">
          {contacts.map((k) => (
            <div
              key={k.id}
              className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-line py-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-base text-ink">{k.fullName}</span>
                <span className="mt-0.5 block text-xs text-muted">
                  {[k.roleTitle, k.email, k.phone].filter(Boolean).join(" · ")}
                </span>
              </span>
              {k.isDecisionMaker && <Mark tone="live">Signs</Mark>}
            </div>
          ))}
        </div>
      )}

      {adding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void invoke();
          }}
          className="mt-5 border border-line-strong p-5"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoComplete="off"
            />
            <Field
              label="Their role"
              hint="Often the thing that tells you whether they can sign."
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              autoComplete="off"
            />
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
            <Field
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="off"
            />
          </div>

          <label className="mt-5 flex items-center gap-2.5 text-base text-ink">
            <input
              type="checkbox"
              checked={isDecisionMaker}
              onChange={(e) => setIsDecisionMaker(e.target.checked)}
              className="h-4 w-4 border border-line bg-control"
            />
            They can sign this off
          </label>

          {refusal && (
            <Failed
              message={refusal.message}
              correlationId={refusal.correlationId}
            />
          )}

          <div className="mt-5 flex gap-3">
            <Button type="submit" tone="primary" loading={pending}>
              Add them
            </Button>
            <Button type="button" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * The six stages, one at a time.
 *
 * One stage visible rather than a wall of thirty boxes: this is read during a
 * conversation, and a form you have to search mid-sentence is a form nobody
 * uses while the client is talking. Every stage saves on leaving it, so a
 * dropped call does not cost the whole record.
 *
 * Draft state lives in this component and is flushed by `save`. The only thing
 * that reloads is the parent, after a successful write — which is why the
 * values below are seeded from `call` once and then owned locally.
 */
function CallForm({
  call,
  people,
  onCompleted,
}: {
  call: IntroCall;
  people: Person[];
  /**
   * Called ONLY when the call is completed, never on an ordinary save.
   *
   * This is the `People.tsx` lesson applied before it bit: the parent reloads
   * on this callback, `useResource`'s reload unmounts the form while it
   * refetches, and a form that unmounts every time it autosaves loses the
   * caret — mid-call, mid-sentence, while a client is talking. Saving is
   * therefore silent and local; only completion, which ends the editing, is
   * allowed to tear the form down.
   */
  onCompleted: () => void;
}) {
  const [stage, setStage] = useState(0);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const s of CALL_OUTLINE) {
      for (const f of s.fields) {
        const v = call[f.key];
        seed[f.key as string] = typeof v === "string" ? v : "";
      }
    }
    return seed;
  });

  // The budget is one answer in four columns, so it is one control.
  const [undecided, setUndecided] = useState(call.budgetUndecided);
  const [low, setLow] = useState(
    call.budgetLow === null ? "" : String(call.budgetLow / 100),
  );
  const [high, setHigh] = useState(
    call.budgetHigh === null ? "" : String(call.budgetHigh / 100),
  );
  const [currency, setCurrency] = useState(call.budgetCurrency ?? "NGN");
  const [nextStepOwner, setNextStepOwner] = useState(
    call.nextStepOwnerUserId ?? "",
  );
  /** Shown instead of reloading, so the form keeps the caret. */
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const { invoke: save, pending: saving, refusal: saveRefusal } = useAction(
    async () => {
      const body: Record<string, unknown> = { ...values };
      body.nextStepOwnerUserId = nextStepOwner || null;
      body.budgetUndecided = undecided;
      if (!undecided) {
        // Naira in, kobo out. The server takes minor units; nothing else in
        // the system stores money any other way.
        body.budgetLow = low.trim() === "" ? null : Math.round(Number(low) * 100);
        body.budgetHigh =
          high.trim() === "" ? null : Math.round(Number(high) * 100);
        body.budgetCurrency = currency.trim().toUpperCase();
      }
      await api.patch<void>(`/api/calls/${call.id}`, body);
      setSavedAt(new Date());
      // Deliberately no reload — see `onCompleted` above.
    },
  );

  const { invoke: complete, pending: completing, refusal: completeRefusal } =
    useAction(async () => {
      // Save first, or completion is judged against what the server last saw
      // rather than what is on the screen — the most confusing possible
      // refusal: "record the next step" with the next step visibly typed in.
      await save();
      await api.post<void>(`/api/calls/${call.id}/complete`, {});
      onCompleted();
    });

  // Computed from what is on screen, not from `call`, so the button reflects
  // what the person has typed rather than the last save.
  const onScreen = useMemo<IntroCall>(
    () => ({
      ...call,
      ...(Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, v.trim() === "" ? null : v]),
      ) as Partial<IntroCall>),
      budgetUndecided: undecided,
      budgetLow: undecided || low.trim() === "" ? null : Math.round(Number(low) * 100),
      budgetHigh:
        undecided || high.trim() === "" ? null : Math.round(Number(high) * 100),
      budgetCurrency: undecided ? null : currency,
    }),
    [call, values, undecided, low, high, currency],
  );
  const missing = missingAnswers(onScreen);

  const set = (key: string, value: string) =>
    setValues((v) => ({ ...v, [key]: value }));

  const current = CALL_OUTLINE[stage]!;
  const done = call.status === "complete";

  const goTo = (next: number) => {
    // Saving on the way out of a stage, so a dropped call costs one stage at
    // worst. Deliberately not debounced-per-keystroke: a half-typed sentence
    // committed to the record every 400ms is noise in the audit trail.
    void save();
    setStage(next);
  };

  return (
    <div className="mt-9">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <Label as="h2">The introductory call · {day(call.calledOn)}</Label>
        <span className="flex items-center gap-2">
          {call.ranByName && (
            <span className="text-xs text-muted">{call.ranByName}</span>
          )}
          <Mark tone={done ? "success" : "warning"}>
            {done ? "Complete" : "Draft"}
          </Mark>
        </span>
      </div>

      {/* The six stages as a strip. Numbered, because knowing where you are in
          a call is part of running one, and a stage carrying an unanswered
          required field is marked so it cannot be lost behind a later one. */}
      <nav aria-label="Call stages" className="mt-5 flex flex-wrap gap-1.5">
        {CALL_OUTLINE.map((s, i) => {
          const owes = s.fields.some(
            (f) => f.required && missing.some((m) => m.key === f.key),
          );
          return (
            <button
              key={s.number}
              type="button"
              onClick={() => goTo(i)}
              aria-current={i === stage ? "step" : undefined}
              className={
                "border px-3 py-2 text-xs transition-colors duration-150 ease-damped " +
                (i === stage
                  ? "border-line-strong bg-control text-ink"
                  : "border-line text-muted hover:border-line-strong hover:text-ink")
              }
            >
              <span className="tabular-nums">{s.number}</span>
              <span className="ml-2">{s.title}</span>
              {owes && <span className="ml-1.5 text-danger">•</span>}
            </button>
          );
        })}
      </nav>

      <Panel className="mt-5 p-6">
        <p className="text-2xs uppercase tracking-[0.28em] text-muted">
          Stage {current.number} of {CALL_OUTLINE.length}
        </p>
        <h3 className="mt-2 font-display text-2xl font-medium tracking-[-0.03em] text-ink">
          {current.title}
        </h3>
        <p className="mt-2 max-w-[62ch] text-xs leading-[1.1rem] text-muted">
          {current.purpose}
        </p>

        {/* Read these, do not paraphrase them into the boxes. The prompts are
            the structure; the boxes are what came back. */}
        <ul className="mt-5 border-l border-line-strong pl-4">
          {current.prompts.map((q) => (
            <li key={q} className="mt-1.5 text-base leading-[1.4rem] text-ink">
              {q}
            </li>
          ))}
        </ul>

        <div className="mt-7 grid gap-5">
          {current.fields.map((f) => (
            <CaptureField
              key={f.key as string}
              field={f}
              value={values[f.key as string] ?? ""}
              onChange={(v) => set(f.key as string, v)}
              missing={missing.some((m) => m.key === f.key)}
              disabled={done}
            />
          ))}

          {/* Stage 4 carries the budget, which is four columns and one
              question, so it is rendered as one control rather than four
              fields that can disagree. */}
          {current.number === 4 && (
            <fieldset className="border border-line p-5">
              <legend className="label px-1">Budget</legend>
              <label className="flex items-center gap-2.5 text-base text-ink">
                <input
                  type="checkbox"
                  checked={undecided}
                  disabled={done}
                  onChange={(e) => setUndecided(e.target.checked)}
                  className="h-4 w-4 border border-line bg-control"
                />
                They have not decided a figure
              </label>
              <p className="mt-1.5 max-w-[62ch] text-xs leading-[1.1rem] text-muted">
                Leave this ticked unless they gave you a number. An invented
                figure later reads as something the client said.
              </p>

              {!undecided && (
                <div className="mt-5 grid gap-5 sm:grid-cols-3">
                  <Field
                    label="From"
                    hint="Whole units, e.g. 2000000"
                    inputMode="numeric"
                    value={low}
                    disabled={done}
                    onChange={(e) => setLow(e.target.value)}
                    autoComplete="off"
                  />
                  <Field
                    label="To"
                    hint="Leave blank if they gave only a floor."
                    inputMode="numeric"
                    value={high}
                    disabled={done}
                    onChange={(e) => setHigh(e.target.value)}
                    autoComplete="off"
                  />
                  <Field
                    label="Currency"
                    hint="Three letters."
                    value={currency}
                    disabled={done}
                    maxLength={3}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                    autoComplete="off"
                  />
                </div>
              )}
            </fieldset>
          )}

          {/* Stage 6 names who owns the next step, from the firm's own people
              — a commitment with no owner is the one that slips. */}
          {current.number === 6 && (
            <>
              <Select
                label="Who owns the next step"
                hint="On our side. The client's own actions belong in what you still need."
                value={nextStepOwner}
                disabled={done}
                onChange={(e) => setNextStepOwner(e.target.value)}
              >
                <option value="">Nobody yet</option>
                {people
                  .filter((p) => p.role !== "client" && !p.isSuspended)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.fullName} · {roleLabel(p.role)}
                    </option>
                  ))}
              </Select>

              <div className="border border-line p-5">
                <Label as="h3">Draft recap</Label>
                <p className="mt-1.5 max-w-[62ch] text-xs leading-[1.1rem] text-muted">
                  Composed from what you have captured. Read it to them, let
                  them correct it, then put what you actually said in the recap
                  box above — this draft is not the record.
                </p>
                <pre className="mt-4 whitespace-pre-wrap font-sans text-base leading-[1.4rem] text-ink">
                  {draftRecap(onScreen)}
                </pre>
                {!done && (
                  <Button
                    type="button"
                    className="mt-4"
                    onClick={() => set("recap", draftRecap(onScreen))}
                  >
                    Use this as a starting point
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </Panel>

      {(saveRefusal ?? completeRefusal) && (
        <Failed
          message={(saveRefusal ?? completeRefusal)!.message}
          correlationId={(saveRefusal ?? completeRefusal)!.correlationId}
        />
      )}

      {!done && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            onClick={() => goTo(Math.max(0, stage - 1))}
            disabled={stage === 0}
          >
            Back
          </Button>
          {stage < CALL_OUTLINE.length - 1 ? (
            <Button tone="primary" onClick={() => goTo(stage + 1)}>
              Next stage
            </Button>
          ) : (
            <Button
              tone="primary"
              loading={completing}
              disabled={missing.length > 0}
              onClick={() => void complete()}
            >
              Complete the call
            </Button>
          )}
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
          {savedAt && !saving && (
            <span className="text-xs text-muted" role="status">
              Saved{" "}
              {savedAt.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}

          {/* Named, not counted. "4 fields missing" makes somebody hunt; the
              list tells them where to go. The database refuses this too — see
              intro_call_complete_is_answered in 050 — and this is the sentence
              a person can act on. */}
          {missing.length > 0 && stage === CALL_OUTLINE.length - 1 && (
            <p className="max-w-[48ch] text-xs leading-[1.1rem] text-muted">
              Before completing: {missing.map((m) => m.label.toLowerCase()).join(", ")}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CaptureField({
  field,
  value,
  onChange,
  missing,
  disabled,
}: {
  field: OutlineField;
  value: string;
  onChange: (v: string) => void;
  missing: boolean;
  disabled: boolean;
}) {
  // A required field that is still blank is marked, but never as an error:
  // nothing has gone wrong mid-call, it simply has not been asked yet.
  const hint = field.required
    ? [field.hint, missing ? "Needed before the call can be completed." : null]
        .filter(Boolean)
        .join(" ")
    : field.hint;

  if (field.kind === "date") {
    return (
      <Field
        label={field.label}
        hint={hint}
        type="date"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.kind === "short") {
    return (
      <Field
        label={field.label}
        hint={hint}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
    );
  }
  return (
    <TextArea
      label={field.label}
      hint={hint}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * The step after the call: turn this prospect into a client and open the matter.
 *
 * **Why this is a link to the engagement form and not a button that converts.**
 * Catch still has no "make them a client" action of its own, and that is the
 * point rather than an omission — a client is born inside
 * `POST /api/engagements`, in the same transaction as the engagement, so one
 * relationship can never become two clients with two permanent filing codes. A
 * second door here would be exactly that second way. What this adds is a short
 * path to the one door, with the prospect already chosen, which is what was
 * actually missing: before it, somebody who had just finished a call had to
 * remember the client's name, go to Engagements, open the form and find them in
 * a dropdown.
 *
 * **Three states, because the honest answer differs.**
 *
 *   * A completed call and the authority to start one — the action, enabled.
 *   * No completed call yet — shown and disabled, with the reason. "After the
 *     full session" was the user's own condition, and it is a good one: the
 *     engagement inherits what the call captured, and a half-filled call makes
 *     a half-understood matter. A disabled control that says why teaches the
 *     sequence; an absent one leaves somebody hunting for it.
 *   * A Project Lead — no action at all, and a line saying who takes it from
 *     here. `engagement_insert` (004) admits only Super Admin and Principal
 *     Partner, so a Lead can run the whole call and then cannot open the
 *     matter. Offering them a link to a form that refuses them would be the
 *     dead end this codebase keeps removing; naming the handover is the useful
 *     thing instead.
 *
 * Nothing here decides authority. `mayStart` mirrors the same check
 * `Engagements.tsx` already makes, and the server refuses regardless.
 */
function TurnIntoEngagement({
  prospect,
  completed,
  compact = false,
  calls = 0,
}: {
  prospect: Prospect;
  /** Whether a call has actually been completed. */
  completed: boolean;
  /** On the list: the action alone, no explanatory prose. */
  compact?: boolean;
  /** How many calls exist at all, for the "hold one first" wording. */
  calls?: number;
}) {
  const { me } = useSession();
  const mayStart =
    me?.role === "super_admin" || me?.role === "principal_partner";

  // Converted or declined: there is nothing to turn. The panel's own banner
  // already says what happened and points at Engagements.
  if (prospect.status !== "open") return null;

  // On the list, a row that is not ready says nothing at all. Twenty rows each
  // explaining why they are not ready is noise, and the detail panel is where
  // somebody has gone to actually act on one.
  if (compact) {
    if (!completed || !mayStart) return null;
    return (
      <Link
        to={`/engagements?prospect=${prospect.id}`}
        className={buttonClasses("secondary") + " min-h-9 px-3 text-xs"}
        onClick={(e) => e.stopPropagation()}
      >
        Turn into an engagement
      </Link>
    );
  }

  if (!mayStart) {
    // Only worth saying once the work is actually done, or it reads as a
    // reprimand for not having finished a call nobody asked them to finish yet.
    if (!completed) return null;
    return (
      <span className="max-w-[26ch] text-right text-xs leading-[1.1rem] text-muted">
        The call is complete. A Principal Partner opens the engagement from
        here.
      </span>
    );
  }

  if (!completed) {
    return (
      <span className="flex flex-col items-end gap-1.5">
        {/* A real disabled button rather than a dead link: there is nowhere
            to go yet, and an <a> with no href is not a control. */}
        <Button tone="secondary" disabled>
          Turn into an engagement
        </Button>
        <span className="max-w-[26ch] text-right text-xs leading-[1.1rem] text-muted">
          {calls === 0
            ? "Hold the introductory call first."
            : "Complete the introductory call first."}
        </span>
      </span>
    );
  }

  return (
    <span className="flex flex-col items-end gap-1.5">
      <Link
        to={`/engagements?prospect=${prospect.id}`}
        className={buttonClasses("primary")}
      >
        Turn into an engagement
      </Link>
      <span className="max-w-[28ch] text-right text-xs leading-[1.1rem] text-muted">
        Opens the engagement form with {prospect.name} already chosen. Starting
        it makes them a client.
      </span>
    </span>
  );
}
